import { RecommendationModel } from "@/app/ec/product/recommendations/recommendations";
import { ProductHit } from "@/components/products-tile/product";
import { createRecommendClient, defaultIndex } from "@/configs/constants";
import { isRootState } from "@/declarations/assertions/isRootState";
import type { AppState } from "@/store/store";
import type { BaseRecommendRequest, RecommendationsRequest } from "@algolia/recommend";
import {
  ActionReducerMapBuilder,
  AsyncThunkConfig,
  GetThunkAPI,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { Hit } from "instantsearch.js";

const initialState: RecommendationsSliceState = {
  recommendations: {},
};

export type RecommendationsSliceState = {
  recommendations: { [key: string]: RecommendationsData | undefined };
};

export type RecommendationsData = {
  loadingState: "complete" | "loading" | "failed";
  hits: Hit<ProductHit>[];
};

export const recommendationsSlice = createSlice({
  name: "recommendationsSlice",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<RecommendationsSliceState>) => {
    builder.addCase(getRecommendations.fulfilled, (state: RecommendationsSliceState, action) => {
      if (action.payload) {
        const { cacheKey, hits } = action.payload;
        state.recommendations[cacheKey] = {
          loadingState: "complete",
          hits: hits as Hit<ProductHit>[],
        };
      }
    });
    builder.addCase(getRecommendations.pending, (state: RecommendationsSliceState, action) => {
      const { model, objectIDs = [], ...modelParams } = action.meta.arg;
      const cacheKey = generateRecommendationsCacheKey(model, objectIDs, modelParams);
      state.recommendations[cacheKey] = {
        loadingState: "loading",
        hits: [],
      };
    });
    builder.addCase(getRecommendations.rejected, (state: RecommendationsSliceState, action) => {
      const { model, objectIDs = [], ...modelParams } = action.meta.arg;
      const cacheKey = generateRecommendationsCacheKey(model, objectIDs, modelParams);
      if (action.meta.aborted) {
        console.log("getRecommendations was aborted: ", action);
      } else {
        const reason = action.payload ?? action.error;
        const message =
          typeof reason === "object" && reason !== null && "message" in reason ? String(reason.message) : "";
        if (message.includes("No recommendations found")) {
          // Expected: Algolia has no data for this index + Recommend model. The component
          // handles it by rendering its fallbackModel, so this isn't a real failure.
          console.warn(`getRecommendations: no recommendations for [model=${model}]; falling back if configured.`);
        } else {
          console.error(`getRecommendations rejected [model=${model}]:`, reason, { cacheKey });
        }
      }
      state.recommendations[cacheKey] = {
        loadingState: "failed",
        hits: [],
      };
    });
  },
});

export interface GetRecommendationsProps extends Omit<
  BaseRecommendRequest,
  "model" | "objectID" | "indexName" | "threshold"
> {
  objectIDs?: string[];
  model: RecommendationModel;
  maxRecommendations?: number;
  threshold?: number;
  // sliderInView: boolean;
}

export const getRecommendations = createAsyncThunk(
  "recommendations/getRecommendations",
  async (
    { model, objectIDs = [], maxRecommendations = 18, ...modelParams }: GetRecommendationsProps,
    thunkApi: GetThunkAPI<AsyncThunkConfig>,
  ) => {
    const cacheKey = generateRecommendationsCacheKey(model, objectIDs, { maxRecommendations, ...modelParams });
    try {
      const payload =
        model === "trending-items"
          ? [
              {
                indexName: defaultIndex,
                maxRecommendations,
                ...modelParams,
                queryParameters: modelParams.queryParameters && {
                  ...modelParams.queryParameters,
                  distinct: 1,
                },
              },
            ]
          : objectIDs.map((objectID) => ({
              indexName: defaultIndex,
              objectID: objectID,
              maxRecommendations,
              ...modelParams,
              queryParameters: modelParams.queryParameters && {
                ...modelParams.queryParameters,
                distinct: 1,
              },
            }));
      const { algoliaAppId, algoliaRecommendationsApiKey } = (thunkApi.getState() as AppState).config;
      const recommendClient = createRecommendClient(algoliaAppId, algoliaRecommendationsApiKey);

      if (payload.length === 0) {
        return thunkApi.abort("getRecommendations Thunk | No payload or method found.");
      }

      const requests = payload.map((p) => ({ threshold: 0, ...p, model }));
      const data = await recommendClient.getRecommendations({ requests: requests as RecommendationsRequest[] });
      // related-products / bought-together fan out one request per objectID, so the
      // results array holds up to (objectIDs × maxRecommendations) hits. De-dupe and
      // cap to maxRecommendations so every model matches trending-items' single-request size.
      const seen = new Set<string>();
      const hits = data.results
        .flatMap((result) => (result as { hits?: (typeof data.results)[0]["hits"] }).hits || [])
        .filter((hit) => {
          const objectID = (hit as { objectID?: string }).objectID;
          if (objectID === undefined || seen.has(objectID)) {
            return false;
          }
          seen.add(objectID);
          return true;
        })
        .slice(0, maxRecommendations);
      return {
        cacheKey,
        hits,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
  {
    condition: ({ model, objectIDs = [], ...modelParams }, { getState }) => {
      const state = getState();
      if (isRootState(state)) {
        const cacheKey = generateRecommendationsCacheKey(model, objectIDs, modelParams);
        const { recommendations } = state.recommendationsSlice;
        if (recommendations[cacheKey]) {
          console.log("getRecommendations Thunk | Recommendations already requested, aborting..");
          return false;
        }
      }
      return true;
    },
  },
);

/**
 * Generates a cache key for recommendations based on the model, objectIDs, and modelParams.
 *
 * This key is used access the recommendaitons cache in the store.
 * @param model
 * @param objectIDs
 * @param modelParams
 * @returns
 */
export function generateRecommendationsCacheKey(
  model: RecommendationModel,
  objectIDs: string[] = [],
  modelParams: Partial<GetRecommendationsProps>,
) {
  // Normalize maxRecommendations (with its default) into a fixed position so every
  // lifecycle handler and selector keys a request identically, and requests with
  // different limits stay distinct (a 5-hit result must not satisfy an 18 request).
  const { maxRecommendations = 18, ...rest } = modelParams;
  const cachableObjectIDs = [...objectIDs];
  cachableObjectIDs.sort(); // Sorting to encourage cache hits.
  return JSON.stringify({ model, objectIDs: cachableObjectIDs, maxRecommendations, ...rest });
}

export const makeGetRecommendationsSelector = () => {
  const selectRecommendations = createSelector(
    [(state: AppState) => state.recommendationsSlice.recommendations, (state: AppState, cacheKey: string) => cacheKey],
    (recommendations: RecommendationsSliceState["recommendations"], cacheKey: string) => recommendations[cacheKey],
  );
  return selectRecommendations;
};
